import React, { useState, useMemo, useEffect } from "react";
import useCourseSets from "../../hooks/useCourseSets";
import useCurriculumSets from "../../hooks/useCurriculumSets";
import { getFirstSetWithSelectedStandards } from "../../modules/utils";
import SelectStandardsModal from "../SelectStandardsModal";
import SelectSetModal from "../SelectSetModal";
import _ from "lodash";
import { getElementTypeLabels } from "UnitPlanDetails/routes/EditUnitPlan/routes/IBPlanner/modules/IBPlannerModule";
import { useI18n } from "Hooks";

import { DATA_GRID_COLUMNS } from "./modules/constants";
import {
  NODE_SOURCE_OPTIONS,
  NODE_SOURCES,
} from "../../modules/stringConstants";

const CourseBasedGradingWrapper = props => {
  const {
    onClose,
    learningCourseId,
    value,
    label,
    plannerElementType: type,
    onSave,
    updateTree,
    initialFilters,
    treeMapRef,
    selectedApplyScope,
  } = props;

  const { t } = useI18n(["common"]);
  const [standardSetId, setStandardSetId] = useState(undefined);
  const [selectedNodesSource, setSelectedNodesSource] = useState(
    NODE_SOURCE_OPTIONS(t)[0]
  );
  const [selectedStandards, setSelectedStandards] = useState(
    new Set(value || [])
  );

  useEffect(() => {
    setSelectedStandards(new Set(value) || []);
  }, [value]);

  const updatedLabel = getElementTypeLabels({ t, type }) || label;

  // when showCourseStandards switcher is toggled
  const {
    idsToPropertiesMap: courseMap,
    showLoader: courseLoader,
    standardEdges: courseEdges,
  } = useCourseSets({ learningCourseId });

  // This fetches the curriculum standard sets which includes all the sets
  const {
    idsToPropertiesMap: curriculumMap,
    showLoader: curriculumLoader,
    standardEdges: curriculumEdges,
  } = useCurriculumSets();

  const showCourseStandards =
    selectedNodesSource?.value === NODE_SOURCES.COURSE;

  const showLoader = showCourseStandards ? courseLoader : curriculumLoader;
  const idsToPropertiesMap = showCourseStandards ? courseMap : curriculumMap;
  const standardEdges = showCourseStandards ? courseEdges : curriculumEdges;

  const firstSetWithSelectedStandards = useMemo(
    () => getFirstSetWithSelectedStandards({ standardEdges, value }),
    [standardEdges, value]
  );

  useEffect(() => {
    if (firstSetWithSelectedStandards) {
      setStandardSetId(prev =>
        prev || prev === null ? prev : firstSetWithSelectedStandards
      );
    }
  }, [firstSetWithSelectedStandards]);

  useEffect(() => {
    setStandardSetId(prev => {
      if (!prev) return prev;
      if (idsToPropertiesMap[prev]) return prev;

      if (_.isEmpty(standardEdges)) return undefined;
      return firstSetWithSelectedStandards;
    });
  }, [firstSetWithSelectedStandards, idsToPropertiesMap, standardEdges]);

  return standardSetId ? (
    <SelectStandardsModal
      onClose={onClose}
      setStandardSetId={setStandardSetId}
      standardSetId={standardSetId}
      label={updatedLabel}
      standardEdges={standardEdges}
      idsToPropertiesMap={idsToPropertiesMap}
      selectedNodesSource={selectedNodesSource}
      setSelectedNodesSource={setSelectedNodesSource}
      learningCourseId={learningCourseId}
      plannerElementType={type}
      columns={DATA_GRID_COLUMNS}
      selectedStandards={selectedStandards}
      setSelectedStandards={setSelectedStandards}
      onSave={onSave}
      updateTree={updateTree}
      initialFilters={initialFilters}
      treeMapRef={treeMapRef}
      selectedApplyScope={selectedApplyScope}
    />
  ) : (
    <SelectSetModal
      onClose={onClose}
      label={updatedLabel}
      setStandardSetId={setStandardSetId}
      standardEdges={standardEdges}
      selectedStandards={selectedStandards}
      selectedNodesSource={selectedNodesSource}
      setSelectedNodesSource={setSelectedNodesSource}
      showLoader={showLoader}
    />
  );
};

export default CourseBasedGradingWrapper;
